/**
 * ToastProvider
 *
 * Lightweight, app-wide non-blocking notifications to replace native alert().
 * Usage:
 *   const toast = useToast();
 *   toast.success('Profile updated');
 *   toast.error('Something went wrong', { duration: 8000 });
 *
 * Toasts auto-dismiss, stack, are dismissable, and announce to screen readers
 * via an aria-live region.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

let toastId = 0;

const VARIANTS = {
  success: { color: 'var(--success-color, #2f9e44)', Icon: FiCheckCircle },
  error: { color: 'var(--error-color, #e03131)', Icon: FiAlertCircle },
  warning: { color: 'var(--warning-color, #f08c00)', Icon: FiAlertTriangle },
  info: { color: 'var(--primary-color)', Icon: FiInfo },
};

const Container = styled.div`
  position: fixed;
  /* Sit below the app header (64px) so a visible toast never overlaps the
     top-right user menu and blocks clicks to it. */
  top: 76px;
  right: 1rem;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-width: min(92vw, 420px);
  pointer-events: none;

  @media (max-width: 600px) {
    top: 66px;
    right: 0.75rem;
    left: 0.75rem;
    max-width: none;
  }
`;

const ToastCard = styled(motion.div)`
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: var(--background-primary, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-left: 4px solid ${(p) => p.$color};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  color: var(--text-primary, #111827);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;

  svg.toast-icon {
    color: ${(p) => p.$color};
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const Message = styled.span`
  flex: 1;
  min-width: 0;
  white-space: pre-line;
  word-break: break-word;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 2px;
  margin: -2px -2px 0 0;
  cursor: pointer;
  color: var(--text-muted, #6b7280);
  border-radius: 6px;

  &:hover {
    color: var(--text-primary, #111827);
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (type, message, opts = {}) => {
      if (message == null || message === '') return null;
      const id = ++toastId;
      const duration = opts.duration != null ? opts.duration : type === 'error' ? 7000 : 4500;
      setToasts((current) => [...current, { id, type, message: String(message) }]);
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      show: (msg, opts) => push('info', msg, opts),
      success: (msg, opts) => push('success', msg, opts),
      error: (msg, opts) => push('error', msg, opts),
      warning: (msg, opts) => push('warning', msg, opts),
      info: (msg, opts) => push('info', msg, opts),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <Container role="region" aria-live="polite" aria-label="Notifications">
          <AnimatePresence initial={false}>
            {toasts.map((t) => {
              const { color, Icon } = VARIANTS[t.type] || VARIANTS.info;
              return (
                <ToastCard
                  key={t.id}
                  $color={color}
                  role="alert"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                >
                  <Icon className="toast-icon" aria-hidden="true" />
                  <Message>{t.message}</Message>
                  <DismissButton onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
                    <FiX />
                  </DismissButton>
                </ToastCard>
              );
            })}
          </AnimatePresence>
        </Container>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export default ToastProvider;
